<template>
  <div class="product-card-wrapper"> <!-- Elemento raíz único -->
    <!-- Skeleton Loading -->
    <div v-if="!imageLoaded" class="product-skeleton">
      <div class="skeleton-image"></div>
      <div class="skeleton-content">
        <div class="skeleton-badge"></div>
        <div class="skeleton-title"></div>
        <div class="skeleton-price"></div>
        <div class="skeleton-button"></div>
      </div>
    </div>

    <!-- Product Card -->
    <div v-else class="product-card">
      <div class="card bg-dark text-white">
        <!-- Discount badge - Modified to display as integer -->
        <div v-if="product.discount > 0" class="discount-badge">
          -{{ Math.round(product.discount) }}%
        </div>

        <!-- Product image -->
        <a :href="`/producto/${product.id}`" class="card-img-wrapper">
          <div class="image-container">
            <img 
              :src="product.image || require('@/assets/zapiola-logo.png')"
              :alt="product.name" 
              class="card-img"
              :class="{ 'loading': !imageLoaded }"
              @load="handleImageLoad"
            >
          </div>
        </a>

        <!-- Product info -->
        <div class="card-body">
          <div class="product-info">
            <div>
              <span class="brand-tag" style="border: 1px solid var(--border-color)">
                {{ product.category_name }}
              </span>
            </div>
            <h3 class="product-name text-white fw-light">{{ product.name }}</h3>
            
            <div class="price-section">
              <!-- Contenedor con altura fija para precios -->
              <div class="price-container">
                <!-- Espacio reservado para precio original aunque no tenga descuento -->
                <div class="original-price-container">
                  <div v-if="product.discount > 0" class="original-price">
                    ${{ formatPrice(product.price) }}
                  </div>
                  <div v-else class="placeholder-price"></div>
                </div>
                <!-- Precio actual siempre en la misma posición -->
                <div class="current-price">
                  ${{ formatPrice(getDiscountedPrice(product)) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="card-actions d-flex">
            <button 
              class="btn btn-quick-view"
              @click="showModal"
              title="Vista rápida"
            >
              <i class="far fa-eye"></i>
            </button>
            <div class="w-100">
              <button 
                class="btn btn-primary w-100"
                @click="handleAddToCart"
                :title="product.stock ? 'Agregar al carrito' : 'Sin stock'"
                :disabled="!product.is_available || product.stock <= 0"
                >
                <i class="fas fa-shopping-cart me-2"></i>
                 {{ product.is_available && product.stock > 0 ? 'Agregar' : 'Sin stock' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick view modal -->
    <Transition name="modal">
      <div v-if="isModalOpen" class="quick-view-modal">
        <div class="modal-backdrop" @click="closeModal"></div>
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <button class="modal-close" @click="closeModal">
                <i class="fas fa-times"></i>
              </button>
            </div>
            
            <div class="modal-body">
              <div class="row">
                <div class="col-md-5">
                  <div class="modal-image-container">
                    <img 
                      :src="product.image || require('@/assets/zapiola-logo.png')"
                      :alt="product.name"
                    >
                  </div>
                </div>
                <div class="col-md-7">
                  <div class="modal-product-info">
                    <div>
                      <div class="brand-tag mb-2">{{ product.category_name }}</div>
                      <!-- Add discount badge in modal if product has discount -->
                      <span v-if="product.discount > 0" class="discount-tag">-{{ Math.round(product.discount) }}% Descuento</span>
                    </div>
                    <h2 class="modal-title mb-3">{{ product.name }}</h2>
                    
                    <div class="description mb-4">
                      <p v-if="product.descripction">{{ product.description }}</p>
                      <p v-else class="text-muted fs-5">No hay una descripción disponible para este producto.</p>
                    </div>
                    
                    <div class="price-section mb-4">
                      <div class="price-container">
                        <div v-if="product.discount > 0" class="original-price me-2">
                          ${{ formatPrice(product.price) }}
                        </div>
                        <div class="current-price">
                          ${{ formatPrice(getDiscountedPrice(product)) }}
                        </div>
                      </div>
                    </div>

                    <button 
                      class="btn btn-primary btn-lg w-100"
                      @click="handleAddToCart"
                      :disabled="!product.is_available || product.stock <= 0"
                      >
                      <i class="fas fa-shopping-cart me-2"></i>
                      {{ product.isAvailable && product.stock > 0 ? 'Agregar' : 'Sin stock' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, defineProps, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { formatPrice } from "@/helpers/product";
import { useStore } from 'vuex'

const props = defineProps({
  product: {
    type: Object,
    required: true,
    default: () => ({
      id: null,
      name: '',
      category: '',
      subcategory: '',
      price: 0,
      stock: 0,
      description: '',
      features: [],
      brand: '',
      image: null,
      discount: 0
    })
  }
})

const isModalOpen = ref(false)
const store = useStore()
const toast = useToast()
const imageLoaded = ref(false)

const showModal = () => {
  isModalOpen.value = true
  document.body.style.overflow = 'hidden'
}

const closeModal = () => {
  isModalOpen.value = false
  document.body.style.overflow = 'auto'
}

const handleAddToCart = () => {
  if (props.product.stock <= 0) {
    toast.error('No hay stock disponible');
    return;
  }

  store.dispatch('addToCart', {
    id: props.product.id,
    name: props.product.name,
    price: Number(props.product.price),
    brand: props.product.brand,
    image: props.product.image,
    quantity: 1
  });
  
  toast.success('Producto añadido al carrito');
}

const handleImageLoad = () => {
  imageLoaded.value = true
}

const getDiscountedPrice = (product) => {
  if (!product.discount || product.discount <= 0) return product.price;
  return product.price * (1 - Math.round(product.discount)/100);
};

onMounted(() => {
  const img = new Image();
  img.src = props.product.image || require('@/assets/zapiola-logo.png');
  img.onload = () => {
    imageLoaded.value = true;
  };
});
</script>

<style scoped>
.product-card-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.product-card {
  position: relative;
  height: 100%; /* Asegurar altura completa */
  display: flex;
  flex-direction: column;
}

.card {
  border: none;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: linear-gradient(145deg, var(--black-color-2), var(--black-color-3));
  overflow: hidden;
  height: 100%; /* Asegurar altura completa */
  display: flex;
  flex-direction: column;
}

.card-img-wrapper {
  width: 100%;
  display: block;
  background-color: white !important;
}

.image-container {
  aspect-ratio: 1;
  background: var(--black-color-2);
  overflow: hidden;
  position: relative;
  width: 100%;
}
.image-container::after{
  /* sombra a final abajo de la imagen */
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40%;
  width: 100%;
  background: linear-gradient(0deg, var(--black-color-2), transparent);
  z-index: 1;
}

.card-img {
  width: 100%;
  height: 100%;
  object-fit: cover; /* Changed from contain to cover to fill the container */
  transition: transform 0.5s ease; /* Slightly longer transition for smoother zoom */
  position:relative;
}


.product-card:hover .card-img {
  transform: scale(1.1); /* Slightly increased scale for more noticeable zoom */
}

.img-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.product-card:hover .img-overlay {
  opacity: 1;
}

.card-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1; /* Permitir que el cuerpo crezca para llenar el espacio */
}

.product-info {
  flex-grow: 1; /* Permitir que la información crezca */
  display: flex;
  flex-direction: column;
}

.brand-tag {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: var(--dark-color);
  color: var(--white-color-2);
  font-size: 0.75rem;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

.product-name {
  font-size: 1rem;
  margin-bottom: 0.5rem;
  line-height: 1.3;
  height: 2.6rem;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.price-section {
  margin: 1rem 0;
  flex-grow: 1; /* Permitir que esta sección se expanda */
  display: flex;
  flex-direction: column;
  justify-content: flex-end; /* Alinear precios en la parte inferior */
}

/* Nueva clase para el contenedor de precios con altura fija */
.price-container {
  height: 45px; /* Altura fija para el contenedor de precios */
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

/* Contenedor para el precio original */
.original-price-container {
  height: 20px; /* Altura para mantener espacio consistente */
  display: flex;
  align-items: center;
}

.placeholder-price {
  height: 1rem; /* Mantener un espacio aunque no haya descuento */
}

.original-price {
  color: var(--text-muted);
  text-decoration: line-through;
  font-size: 0.9rem;
}

.current-price {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--primary-color);
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: auto; /* Empujar las acciones hacia la parte inferior */
}

.btn-quick-view,
.btn-cart {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.btn-quick-view {
  background: var(--black-color-2);
  color: var(--white-color);
}

.discount-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: var(--green-color);
  color: var(--white-color);
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.875rem;
  z-index: 2;
}

/* Add style for discount tag in modal */
.discount-tag {
  display: inline-block;
  background: var(--green-color);
  color: var(--white-color);
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.85rem;
  margin-left: 0.5rem;
}

/* Quick view modal styles */
.quick-view-modal {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.8);
  backdrop-filter: blur(5px);
  z-index: 1060;
}

.modal-dialog {
  position: relative;
  width: 95%;
  max-width: 1000px;
  max-height: 90vh;
  margin: 1.75rem auto;
  z-index: 1070;
}

.modal-content {
  background: var(--black-color-2);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.modal-header {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
}

.modal-close {
  background: none;
  border: none;
  color: var(--white-color);
  font-size: 1.5rem;
  padding: 0.5rem;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.modal-close:hover {
  transform: scale(1.1);
}

.modal-body {
  padding: 2rem;
}

.modal-image-container {
  background: var(--black-color-3);
  border-radius: 8px;
  padding: 2rem;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-image-container img {
  max-width: 100%;
  height: auto;
  object-fit: contain;
}

.modal-product-info {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.modal-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--white-color);
}

.features-title {
  font-size: 1.1rem;
  color: var(--white-color);
  font-weight: 500;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-item {
  color: var(--white-color-2);
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.feature-item i {
  color: var(--primary-color);
}

/* Modal animations */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .modal-dialog {
    width: 95%;
    margin: 1rem auto;
  }

  .modal-body {
    padding: 1rem;
  }

  .modal-image-container {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }

  .modal-title {
    font-size: 1.5rem;
  }
}

.category-tag {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: var(--border-color);
  color: var(--white-color-2);
  font-size: 0.75rem;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  margin-left: 0.5rem;
}

/* Skeleton Styles */
.product-skeleton {
    background: var(--black-color-2);
    border-radius: 12px;
    padding: 1rem;
    height: 100%;
    border: 1px solid var(--border-color);
}

.skeleton-image {
    width: 100%;
    height: 200px;
    background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 8px;
    margin-bottom: 1rem;
}

.skeleton-badge {
    width: 60px;
    height: 24px;
    background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 4px;
    margin-bottom: 0.5rem;
}

.skeleton-title {
    width: 100%;
    height: 20px;
    background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 4px;
    margin-bottom: 1rem;
}

.skeleton-price {
    width: 70%;
    height: 24px;
    background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 4px;
    margin-bottom: 1rem;
}

.skeleton-button {
    width: 100%;
    height: 40px;
    background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 8px;
}

@keyframes loading {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}
</style>